import { formatWeekDate, getShipDateByDays } from '../shipping/index.js';
import { calculatePackageNumber } from './package.js';
import { LeadtimeDisplayMap } from '../constants/index.js';
import { getDfmSelectionsString } from '../models/quotes/getFeedbackSelections.js';
import calculateV3 from './v3.js';
import calculateUnitPriceV0 from './v0.js';

const calculateUnitPrice = ({ quote, options }) => {
  switch (quote.pricingVersion) {
    case 'v3':
      return calculateV3({ quote, options });
    default: {
      // use the original pricing formula
      const { rates, leadtimeMultiples } = quote;
      const {
        material, cappingStyle, numConnectors, quantity, delivery,
      } = options;
      return calculateUnitPriceV0(
        rates,
        material,
        cappingStyle,
        numConnectors,
        quantity,
        delivery,
        leadtimeMultiples,
      );
    }
  }
};

const calculateCartPrice = ({ items }) => {
  // validate items is an non empty array
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items must be a non-empty array');
  }

  // Calculate the total price of the cart based on the items.
  // Each item should have a `unitPrice` and `quantity` property.

  const setupFeesMap = {};
  // Setup is charged once per part CONFIGURATION: two cart items for the same part
  // with different DFM selections each need their own tooling, so each carries its
  // own setup fee, while duplicates of the same configuration share one.
  const setupKey = (item) => [
    item.partID,
    getDfmSelectionsString(item.feedbackResponses ?? item.quote?.feedbackResponses),
  ].join('|');

  const itemsWithUnitPrice = items.map((item) => {
    const {
      id, cappingStyle, quantity, delivery, quote, feedbackResponses,
    } = item;

    if (typeof quote !== 'object' || !quote) {
      throw new Error('Each item must have a quote object');
    }

    if (!id || !cappingStyle || !quantity || !delivery) {
      throw new Error('Each item must have cappingStyle, quantity, and delivery properties');
    }

    const {
      unitPrice,
      leadtimeText,
      leadtimeDays,
      setupPrice: initialSetupPrice,
      discount,
    } = calculateUnitPrice({
      quote,
      options: {
        cappingStyle: cappingStyle.toLowerCase(),
        quantity,
        delivery,
        feedbackResponses,
      },
    });

    let setupPrice = initialSetupPrice;
    if (discount) {
      setupPrice -= discount;
      if (setupPrice < 0) {
        setupPrice = 0;
      }
    }

    // calculate shipping text
    let shippingText = '';
    let targetShipDate = null;
    if (leadtimeText) {
      shippingText = `${LeadtimeDisplayMap[delivery]}: ${leadtimeText}`;
    } else {
      targetShipDate = getShipDateByDays(leadtimeDays);
      shippingText = `${LeadtimeDisplayMap[delivery]}: Ships ${formatWeekDate(targetShipDate)}`;
    }

    if (!setupFeesMap[setupKey(item)] || setupPrice > setupFeesMap[setupKey(item)]?.price) {
      setupFeesMap[setupKey(item)] = {
        price: setupPrice,
        id,
      };
    }

    return {
      ...item,
      shippingText,
      targetShipDate,
      unitPrice,
      leadtimeText,
      leadtimeDays,
    };
  });

  const itemsWithPrice = itemsWithUnitPrice.map((item) => {
    const {
      unitPrice, quantity, id,
    } = item;

    let setupPrice = null;
    if (setupFeesMap[setupKey(item)]?.id === id) {
      setupPrice = setupFeesMap[setupKey(item)].price;
    }

    return {
      ...item,
      setupPrice,
      price: unitPrice * quantity + setupPrice,
    };
  });

  return itemsWithPrice;
};

export {
  calculateUnitPrice,
  calculateCartPrice,
  calculatePackageNumber,
};
